import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Paper, Card, CardContent, CardActions, Chip, IconButton, Tooltip,
  Avatar, Divider, Stack, LinearProgress, Alert, Snackbar, CircularProgress,
  Fade, Zoom
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ScienceIcon from '@mui/icons-material/Science';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import axios from 'axios';
import Loading from '../components/Loading';
import API_URL from '../config';

const REPORT_ENDPOINTS = {
  'GPR Report':        { endpoint: '/generate_gpr',          single: true,  color: '#1F79B1', icon: '📡', category: 'Testing' },
  'HCP Report':        { endpoint: '/generate_hcp',          single: false, color: '#1F79B1', icon: '🧪', category: 'Testing' },
  'ESR Report':        { endpoint: '/generate_esr',          single: false, color: '#1F79B1', icon: '⚗️', category: 'Testing' },
  'ESR CORES Report':  { endpoint: '/generate_esr_cores',    single: false, color: '#1F79B1', icon: '🪨', category: 'Testing' },
  'UPV Report':        { endpoint: '/generate_upv',          single: false, color: '#1F79B1', icon: '🔊', category: 'Testing' },
  'UPV CORES Report':  { endpoint: '/generate_upv_cores',    single: false, color: '#1F79B1', icon: '🔬', category: 'Testing' },
  'SRH Report':        { endpoint: '/generate_srh',          single: false, color: '#1F79B1', icon: '💧', category: 'Testing' },
  'ECHO Report':       { endpoint: '/generate_echo',         single: false, color: '#1F79B1', icon: '📐', category: 'Testing' },
  'CARBO Report':      { endpoint: '/generate_carbo',        single: true,  color: '#1F79B1', icon: '🧱', category: 'Chemical' },
  'PH Report':         { endpoint: '/generate_ph',           single: true,  color: '#1F79B1', icon: '⚖️', category: 'Chemical' },
  'Sulfate Report':    { endpoint: '/generate_sulfate',      single: true,  color: '#1F79B1', icon: '🧂', category: 'Chemical' },
  'Water Report':      { endpoint: '/generate_water',        single: true,  color: '#1F79B1', icon: '💦', category: 'Chemical' },
  'RCPT Report':       { endpoint: '/generate_rcpt',         single: true,  color: '#1F79B1', icon: '📊', category: 'Chemical' },
  'Chloride Report':   { endpoint: '/generate_chloride',     single: true,  color: '#1F79B1', icon: '🧴', category: 'Chemical' },
};

function RNDReport() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/ai-reports/reports`);
      setReports(response.data);
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError('Failed to load reports. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (reportName) => {
    setSelectedReport(reportName);
    setSelectedFiles([]);
    setDownloadUrl(null);
    setGenerating(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedReport(null);
    setSelectedFiles([]);
    setDownloadUrl(null);
    setGenerating(false);
    setDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileInputChange = (e) => {
    handleFiles(Array.from(e.target.files));
  };

  const handleFiles = (files) => {
    const validFiles = files.filter(file => file.name.match(/\.(xlsx|xls|xlsm)$/i));
    if (validFiles.length !== files.length) {
      setSnackbar({ open: true, message: 'Some files were skipped — only Excel files (.xlsx, .xls, .xlsm) are allowed.', severity: 'warning' });
    }
    if (validFiles.length === 0) return;

    const config = REPORT_ENDPOINTS[selectedReport];
    if (config && config.single) {
      setSelectedFiles([validFiles[0]]);
    } else {
      setSelectedFiles(prev => [...prev, ...validFiles]);
    }
    setDownloadUrl(null);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setDownloadUrl(null);
  };

  const handleGenerate = async () => {
    if (selectedFiles.length === 0) return;

    const config = REPORT_ENDPOINTS[selectedReport];
    if (!config) return;

    setGenerating(true);
    setError(null);

    const formData = new FormData();
    if (config.single) {
      formData.append('excel_file', selectedFiles[0]);
    } else {
      selectedFiles.forEach(file => {
        formData.append('excel_files', file);
      });
    }

    try {
      const response = await axios.post(`${API_URL}/ai-reports${config.endpoint}`, formData, {
        responseType: 'blob',
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      setDownloadUrl(url);
      setSnackbar({ open: true, message: 'Report generated successfully!', severity: 'success' });

      // Log the report generation
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const fileNames = selectedFiles.map(f => f.name).join(', ');
          await axios.post(`${API_URL}/logs`, {
            module: 'AI Report',
            action: 'GENERATE',
            entity_id: selectedReport,
            details: `Generated "${selectedReport}" with file(s): ${fileNames}`,
            source: 'Web'
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
      } catch (logErr) {
        console.error('Failed to save audit log:', logErr);
      }
    } catch (err) {
      console.error('Error generating report:', err);
      let errorMsg = 'Failed to generate report. Please try again.';
      if (err.response && err.response.data) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          errorMsg = json.message || errorMsg;
        } catch (_) {}
      }
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (downloadUrl) {
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${selectedReport.toLowerCase().replace(/\s+/g, '_')}_report.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getReportIcon = (reportName) => {
    const config = REPORT_ENDPOINTS[reportName];
    return config ? config.icon : '📄';
  };

  const getReportColor = (reportName) => {
    const config = REPORT_ENDPOINTS[reportName];
    return config ? config.color : '#64748b';
  };

  const getReportGradient = (reportName) => {
    const config = REPORT_ENDPOINTS[reportName];
    const color = config ? config.color : '#64748b';
    return `linear-gradient(135deg, ${color}, ${color}dd)`;
  };

  const reportList = reports.length > 0
    ? reports
    : Object.keys(REPORT_ENDPOINTS).map(name => ({ name, description: 'Generate report from Excel data' }));

  if (loading) return <Loading />;

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>

      {/* Gradient Header */}
      <Zoom in={true} timeout={600}>
        <Box sx={{
          mb: 4, mt: 2, p: { xs: 3, md: 4 }, borderRadius: 4,
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 40%, #1e3c72 100%)',
          color: 'white',
          boxShadow: '0 10px 40px rgba(15, 23, 42, 0.35)',
          position: 'relative', overflow: 'hidden',
          display: 'flex', flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 3,
        }}>
          {/* Decorative circles */}
          <Box sx={{
            position: 'absolute', top: -60, right: -40, width: 220, height: 220,
            borderRadius: '50%', background: 'rgba(59,130,246,0.12)', pointerEvents: 'none'
          }} />
          <Box sx={{
            position: 'absolute', bottom: -80, left: '20%', width: 180, height: 180,
            borderRadius: '50%', background: 'rgba(139,92,246,0.08)', pointerEvents: 'none'
          }} />

          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Typography variant="h4" fontWeight="800" sx={{ mb: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
              <AutoAwesomeIcon sx={{ fontSize: 34, color: '#60a5fa' }} />
              AI Report
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.75)', maxWidth: 500 }}>
              Upload Excel files to generate AI-powered R&D reports — fast, accurate, and fully automated.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, position: 'relative', zIndex: 2 }}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchReports}
              sx={{
                borderRadius: 2.5, fontWeight: 700, px: 3, py: 1.2,
                bgcolor: 'rgba(255,255,255,0.15)', color: 'white',
                textTransform: 'none', backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.15)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)', border: '1px solid rgba(255,255,255,0.25)' }
              }}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Zoom>

      {/* Error Alert */}
      <Fade in={!!error} timeout={300}>
        <Box>{error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}</Box>
      </Fade>


      {/* Report Cards Grid */}
      <Grid container spacing={2}>
        {reportList.map((report, idx) => {
          const color = getReportColor(report.name);
          const gradient = getReportGradient(report.name);
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={report.name}>
              <Zoom in={true} timeout={300 + idx * 60} style={{ transitionDelay: `${idx * 40}ms` }}>
                <Card elevation={0} sx={{
                  height: '100%', display: 'flex', flexDirection: 'column',
                  borderRadius: 4, bgcolor: '#ffffff',
                  border: '1px solid #f1f5f9',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative', overflow: 'hidden',
                  '&::before': {
                    content: '""', position: 'absolute', top: 0, left: 0,
                    width: '100%', height: '4px', background: gradient,
                  },
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: '0 18px 35px rgba(0,0,0,0.1)',
                    borderColor: `${color}40`,
                  }
                }}>
                  <CardContent sx={{ flexGrow: 1, pb: 1, pt: 3 }}>
                    <Stack direction="row" alignItems="flex-start" spacing={1.5} mb={1.5}>
                      <Avatar variant="rounded" sx={{
                        width: 44, height: 44, borderRadius: 2.5,
                        bgcolor: `${color}15`, color: color,
                        fontSize: '1.4rem', flexShrink: 0,
                        boxShadow: `0 4px 10px ${color}20`,
                      }}>
                        {getReportIcon(report.name)}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1.2, mb: 0.3 }}>
                          {report.name}
                        </Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                          <Chip
                            label={REPORT_ENDPOINTS[report.name]?.single ? 'Single' : 'Multi'}
                            size="small"
                            sx={{
                              height: 20, fontSize: '0.65rem', fontWeight: 700,
                              bgcolor: REPORT_ENDPOINTS[report.name]?.single ? '#ecfdf5' : '#f5f3ff',
                              color: REPORT_ENDPOINTS[report.name]?.single ? '#059669' : '#7c3aed',
                            }}
                          />
                          {REPORT_ENDPOINTS[report.name]?.category && (
                            <Chip
                              label={REPORT_ENDPOINTS[report.name].category}
                              size="small"
                              sx={{
                                height: 20, fontSize: '0.65rem', fontWeight: 700,
                                bgcolor: '#eff6ff', color: '#2563eb',
                              }}
                            />
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                    <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.78rem', lineHeight: 1.5 }}>
                      {report.description || 'Upload Excel data and generate an AI-powered report in seconds.'}
                    </Typography>
                  </CardContent>
                  <Divider sx={{ borderColor: '#f1f5f9' }} />
                  <CardActions sx={{ px: 2.5, py: 2 }}>
                    <Button
                      fullWidth variant="contained" size="medium"
                      startIcon={<CloudUploadIcon sx={{ fontSize: 18 }} />}
                      onClick={() => handleOpenDialog(report.name)}
                      sx={{
                        background: gradient, color: 'white',
                        borderRadius: 2.5, fontWeight: 700,
                        textTransform: 'none', fontSize: '0.82rem',
                        boxShadow: `0 4px 14px ${color}40`,
                        py: 1,
                        '&:hover': {
                          background: gradient, filter: 'brightness(0.88)',
                          boxShadow: `0 6px 20px ${color}50`,
                        },
                      }}
                    >
                      Generate Report
                    </Button>
                  </CardActions>
                </Card>
              </Zoom>
            </Grid>
          );
        })}
      </Grid>

      {/* Empty State */}
      {reportList.length === 0 && !loading && (
        <Box sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          py: 10, border: '2px dashed #e2e8f0', borderRadius: 4, bgcolor: '#ffffff', mt: 2,
        }}>
          <ScienceIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
          <Typography variant="h6" fontWeight={700} color="#94a3b8">No Reports Found</Typography>
          <Typography variant="body2" color="#cbd5e1" mt={0.5}>Try selecting a different category tab.</Typography>
        </Box>
      )}

      {/* Upload Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={(_, reason) => { if (reason !== 'backdropClick') handleCloseDialog(); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
        TransitionComponent={Fade}
        transitionDuration={300}
      >
        <DialogTitle sx={{
          background: getReportGradient(selectedReport || ''),
          color: 'white', py: 2.5, px: 3,
        }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar variant="rounded" sx={{
              width: 40, height: 40, borderRadius: 2.5,
              bgcolor: 'rgba(255,255,255,0.2)', color: 'white',
              fontSize: '1.3rem',
            }}>
              {getReportIcon(selectedReport || '')}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} color="white" lineHeight={1.2}>
                {selectedReport}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {REPORT_ENDPOINTS[selectedReport || '']?.single ? 'Upload a single Excel file' : 'Upload one or more Excel files'}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2, px: 3 }}>
          {/* Drop Zone */}
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById('rnd-file-input')?.click()}
            sx={{
              border: `2px dashed ${dragging ? '#3b82f6' : '#e2e8f0'}`,
              borderRadius: 4, p: 4.5, textAlign: 'center', cursor: 'pointer',
              bgcolor: dragging ? '#eff6ff' : '#fafbfc',
              transition: 'all 0.25s ease',
              '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc', transform: 'scale(1.01)' },
              mb: 2.5,
            }}
          >
            <input
              type="file" id="rnd-file-input"
              accept=".xlsx,.xls,.xlsm"
              multiple={!REPORT_ENDPOINTS[selectedReport || '']?.single}
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
            <Box sx={{
              width: 64, height: 64, borderRadius: '50%', bgcolor: dragging ? '#dbeafe' : '#f1f5f9',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mx: 'auto', mb: 2, transition: 'all 0.2s',
            }}>
              <CloudUploadIcon sx={{ fontSize: 32, color: dragging ? '#2563eb' : '#94a3b8' }} />
            </Box>
            <Typography variant="subtitle1" fontWeight={700} color="#1e293b" mb={0.5}>
              {dragging ? 'Drop your files here' : 'Drag & drop Excel files or click to browse'}
            </Typography>
            <Typography variant="caption" color="#94a3b8">
              .xlsx, .xls, .xlsm — max 25MB per file
            </Typography>
          </Box>

          {/* Selected Files */}
          {selectedFiles.length > 0 && (
            <Fade in={true} timeout={300}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" fontWeight={700} color="#475569" mb={1} display="block">
                  Selected Files ({selectedFiles.length})
                </Typography>
                <Stack spacing={1}>
                  {selectedFiles.map((file, index) => (
                    <Paper key={`${file.name}-${index}`} variant="outlined" sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      p: 1.5, borderRadius: 2.5, borderColor: '#e2e8f0', bgcolor: '#fafcfd',
                    }}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box sx={{
                          width: 36, height: 36, borderRadius: 2, bgcolor: '#ecfdf5',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <InsertDriveFileIcon sx={{ color: '#10b981', fontSize: 18 }} />
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={600} color="#1e293b">{file.name}</Typography>
                          <Typography variant="caption" color="#94a3b8">{(file.size / 1024).toFixed(1)} KB</Typography>
                        </Box>
                      </Stack>
                      <IconButton size="small" onClick={() => handleRemoveFile(index)}
                        sx={{ color: '#f87171', '&:hover': { bgcolor: '#fef2f2' } }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            </Fade>
          )}

          {/* Generating */}
          {generating && (
            <Box sx={{ mb: 2, textAlign: 'center', py: 2 }}>
              <CircularProgress size={40} sx={{ color: getReportColor(selectedReport || ''), mb: 1.5 }} />
              <Typography variant="body2" fontWeight={600} color="#475569">
                Generating your report...
              </Typography>
              <Typography variant="caption" color="#94a3b8">This may take a moment</Typography>
            </Box>
          )}

          {/* Download */}
          {downloadUrl && !generating && (
            <Fade in={true} timeout={400}>
              <Button fullWidth variant="contained" size="large"
                startIcon={<DownloadIcon />} onClick={handleDownload}
                sx={{
                  bgcolor: '#10b981', color: 'white', borderRadius: 2.5,
                  fontWeight: 700, textTransform: 'none', py: 1.4,
                  boxShadow: '0 4px 14px rgba(16,185,129,0.4)',
                  '&:hover': { bgcolor: '#059669', boxShadow: '0 6px 20px rgba(16,185,129,0.5)' },
                }}>
                Download Generated Report
              </Button>
            </Fade>
          )}
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid #f1f5f9', px: 3, py: 2.5, bgcolor: '#fafbfc', gap: 1 }}>
          <Button onClick={handleCloseDialog}
            sx={{ color: '#64748b', fontWeight: 600, textTransform: 'none', borderRadius: 2 }}>
            Cancel
          </Button>
          <Button variant="contained"
            disabled={selectedFiles.length === 0 || generating}
            onClick={handleGenerate}
            startIcon={<AutoAwesomeIcon />}
            sx={{
              background: getReportGradient(selectedReport || ''),
              color: 'white', borderRadius: 2.5, fontWeight: 700,
              textTransform: 'none', px: 3,
              boxShadow: `0 4px 14px ${getReportColor(selectedReport || '')}40`,
              '&:hover': { filter: 'brightness(0.88)', boxShadow: `0 6px 20px ${getReportColor(selectedReport || '')}50` },
              '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8', boxShadow: 'none' },
            }}>
            {generating ? 'Generating...' : 'Upload & Generate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity} variant="filled"
          sx={{ borderRadius: 2.5, fontWeight: 700, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default RNDReport;